import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { CameraDeniedError, type ScanSession, type ZoomRange } from '@/application/ports/Scanner';
import { useKitRepository, useScanner } from '@/application/container';
import { UnknownCaseError, useFindCase } from '@/application/usecases/useKits';
import { strings } from '@/shared/strings';
import { CameraIcon, CheckIcon, CloseIcon, KeyIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { platform, signalScan, unlockSound } from './feedback';
import styles from './ScanScreen.module.css';

/** The same code again inside this window is the same tray, not a second scan. */
const REPEAT_MS = 3000;
/** After an unknown code, how long before scanning starts again. */
const RETRY_MS = 900;

/** Where a typed or scanned value leads: a job order, a kit, or a printed tray code. */
type Target = { kind: 'order'; jobOrderNo: string } | { kind: 'kit'; kitId: string } | { kind: 'code'; code: string };

function targetOf(raw: string): Target {
  const value = raw.trim().toUpperCase();
  const order = /^JO-?(\d+)$/.exec(value) ?? /^(\d{3,})$/.exec(value);
  if (order !== null) return { kind: 'order', jobOrderNo: `JO-${order[1] ?? ''}` };
  if (/^K-\d+$/.test(value)) return { kind: 'kit', kitId: value };
  return { kind: 'code', code: value };
}

/**
 * The QR scanner. Section 11.3, and the request of 4 October 2026 (second),
 * section 5: quick on an iPhone in ordinary lab light.
 *
 * The camera starts the moment the screen opens. A code under the frame is
 * read several times a second; on a read the phone buzzes and ticks, decoding
 * stops, and the case opens. The same code is never taken twice in a row. An
 * unknown code says so and scanning carries on, because the next tray is in
 * the technician's hand. Torch where the phone has one, zoom by slider or by
 * pinching, and two ways round a camera that will not do: the job order number
 * typed in, or a photograph of the code.
 *
 * Codes printed by the lab are the bare value string, never a URL: shorter, so
 * the symbol is smaller and reads more easily off a toner starved print.
 */
export function ScanScreen() {
  const scanner = useScanner();
  const kits = useKitRepository();
  const find = useFindCase();
  const navigate = useNavigate();

  const video = useRef<HTMLVideoElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const session = useRef<ScanSession | null>(null);
  const lastValue = useRef<{ value: string; at: number } | null>(null);
  const busy = useRef(false);
  const pinch = useRef<{ distance: number; zoom: number } | null>(null);
  const touches = useRef(new Map<number, { x: number; y: number }>());

  const [mode, setMode] = useState<'camera' | 'manual' | 'denied'>('camera');
  const [attempt, setAttempt] = useState(0);
  const [manualValue, setManualValue] = useState('');
  const [status, setStatus] = useState<string>(strings.scan.scanning);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [zoomRange, setZoomRange] = useState<ZoomRange | null>(null);
  const [zoom, setZoom] = useState(1);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // A code was read: the frame turns green with a tick while the case opens.
  const [read, setRead] = useState(false);

  const open = useMutation({
    mutationFn: async (target: Target): Promise<string> => {
      if (target.kind === 'kit') return target.kitId;
      if (target.kind === 'order') {
        // A case with no tray behind it, a delivered one from before the system,
        // has nothing to open on a bench: it reads as a code that names nothing.
        const { kitId } = await find.mutateAsync(target.jobOrderNo);
        if (kitId === null) throw new UnknownCaseError();
        return kitId;
      }
      return (await kits.byQr(target.code)).id;
    },
    onSuccess: (kitId) => {
      session.current?.stop();
      session.current = null;
      // The tray was scanned, which is what entitles this person to take it.
      void navigate(`/queue/${kitId}`, { state: { scanned: true } });
    },
    onError: () => {
      // Keep scanning. The technician is standing at the bench with the next
      // tray in their hand, and dropping them to another screen is a step back.
      setStatus(strings.scan.unknown);
      setNotFound(true);
      setRead(false);
      setTimeout(() => {
        busy.current = false;
        session.current?.resume();
      }, RETRY_MS);
    },
  });

  const { mutate: openTarget } = open;
  const handleValue = useCallback(
    (value: string) => {
      const previous = lastValue.current;
      if (busy.current) return;
      if (previous !== null && previous.value === value && Date.now() - previous.at < REPEAT_MS) return;
      lastValue.current = { value, at: Date.now() };
      busy.current = true;
      session.current?.pause();
      signalScan();
      setRead(true);
      setNotFound(false);
      setStatus(strings.scan.opening(value));
      openTarget(targetOf(value));
    },
    [openTarget],
  );

  useEffect(() => {
    if (mode !== 'camera') return;
    let cancelled = false;

    if (!scanner.isSupported()) {
      setMode('manual');
      return;
    }
    const element = video.current;
    if (element === null) return;

    void scanner
      .start({
        video: element,
        onResult: handleValue,
        onError: () => undefined,
      })
      .then((started) => {
        if (cancelled) {
          started.stop();
          return;
        }
        session.current = started;
        setTorchAvailable(started.torchAvailable);
        setZoomRange(started.zoom);
        setZoom(started.zoom.min);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setMode(error instanceof CameraDeniedError ? 'denied' : 'manual');
      });

    return () => {
      cancelled = true;
      session.current?.stop();
      session.current = null;
    };
  }, [mode, attempt, scanner, handleValue]);

  function changeZoom(value: number) {
    if (zoomRange === null) return;
    const next = Math.min(zoomRange.max, Math.max(zoomRange.min, value));
    setZoom(next);
    void session.current?.setZoom(next);
  }

  // Pinching the picture zooms it, as it does in the camera app.
  function pointerDown(event: React.PointerEvent) {
    unlockSound();
    touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (touches.current.size === 2) {
      const [a, b] = [...touches.current.values()];
      if (a !== undefined && b !== undefined) pinch.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom };
    }
  }
  function pointerMove(event: React.PointerEvent) {
    if (!touches.current.has(event.pointerId)) return;
    touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const start = pinch.current;
    if (start === null || touches.current.size !== 2) return;
    const [a, b] = [...touches.current.values()];
    if (a === undefined || b === undefined || start.distance === 0) return;
    changeZoom(start.zoom * (Math.hypot(a.x - b.x, a.y - b.y) / start.distance));
  }
  function pointerUp(event: React.PointerEvent) {
    touches.current.delete(event.pointerId);
    if (touches.current.size < 2) pinch.current = null;
  }

  async function readPhoto(file: File) {
    setPhotoBusy(true);
    setNotFound(false);
    setStatus(strings.scan.readingPhoto);
    try {
      const value = await scanner.decodeImage(file);
      if (value === null) {
        setStatus(strings.scan.noCodeInPhoto);
        setNotFound(true);
      } else {
        lastValue.current = null;
        busy.current = false;
        handleValue(value);
      }
    } catch {
      setStatus(strings.scan.noCodeInPhoto);
      setNotFound(true);
    } finally {
      setPhotoBusy(false);
    }
  }

  function submitManual() {
    if (manualValue.trim() === '') return;
    openTarget(targetOf(manualValue));
  }

  const photo = (
    <>
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        aria-label={strings.scan.photoLabel}
        className={styles.fileInput}
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked !== undefined) void readPhoto(picked);
          event.target.value = '';
        }}
      />
      <Button
        className={mode === 'camera' ? styles.ghost : undefined}
        icon={<CameraIcon />}
        disabled={photoBusy}
        onClick={() => photoInput.current?.click()}
      >
        {photoBusy ? strings.scan.readingPhoto : strings.scan.fromPhoto}
      </Button>
    </>
  );

  if (mode !== 'camera') {
    const kind = platform();
    const steps =
      kind === 'iphone'
        ? strings.scan.stepsIphone
        : kind === 'iphoneInstalled'
          ? strings.scan.stepsIphoneInstalled
          : kind === 'android'
            ? strings.scan.stepsAndroid
            : strings.scan.stepsOther;
    return (
      <div className={styles.screen}>
        <div className={styles.manual}>
          {mode === 'denied' ? (
            <>
              <h2>{strings.scan.deniedTitle}</h2>
              <p>{strings.scan.deniedLead}</p>
              <ol className={styles.steps}>
                {steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </>
          ) : (
            <>
              <h2>{strings.scan.manualTitle}</h2>
              <p>{strings.scan.manualLead}</p>
            </>
          )}

          <label htmlFor="manual-code">{strings.scan.manualLabel}</label>
          <input
            id="manual-code"
            value={manualValue}
            placeholder={strings.scan.manualPlaceholder}
            autoComplete="off"
            autoCapitalize="characters"
            inputMode="text"
            onChange={(event) => setManualValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submitManual();
            }}
          />

          {(open.isError || notFound) && (
            <p role="alert" className={styles.alert}>
              {open.isError ? strings.scan.unknown : status}
            </p>
          )}

          <div className={styles.manualActions}>
            <Button onClick={() => void navigate(-1)}>{strings.scan.cancel}</Button>
            <Button variant="primary" onClick={submitManual} disabled={open.isPending}>
              {strings.scan.manualSubmit}
            </Button>
          </div>
          <div className={styles.otherWays}>
            {photo}
            {scanner.isSupported() && (
              <Button
                onClick={() => {
                  setAttempt((n) => n + 1);
                  setMode('camera');
                }}
              >
                {mode === 'denied' ? strings.scan.tryAgain : strings.scan.backToCamera}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const digital = zoomRange !== null && !zoomRange.hardware;

  return (
    <div
      className={styles.screen}
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerUp}
      onPointerCancel={pointerUp}
    >
      <video
        ref={video}
        className={styles.video}
        style={digital ? { transform: `scale(${zoom})` } : undefined}
        playsInline
        muted
        autoPlay
      />

      <button
        type="button"
        className={styles.close}
        aria-label={strings.scan.cancel}
        onClick={() => void navigate(-1)}
      >
        <CloseIcon size={22} />
      </button>

      <div className={`${styles.frame} ${read ? styles.read : ''}`} aria-hidden="true">
        {read ? <CheckIcon size={44} className={styles.tick} /> : <span className={styles.laser} />}
      </div>

      <p className={styles.lead}>{strings.scan.lead}</p>
      <p className={`${styles.status} ${notFound ? styles.warn : ''}`} aria-live="polite">
        {status}
      </p>

      {zoomRange !== null && (
        <label className={styles.zoom}>
          <span>{strings.scan.zoom}</span>
          <input
            type="range"
            min={zoomRange.min}
            max={zoomRange.max}
            step={zoomRange.step}
            value={zoom}
            aria-label={strings.scan.zoom}
            onChange={(event) => changeZoom(Number(event.target.value))}
          />
          <b>{zoom.toFixed(1)}×</b>
        </label>
      )}

      <div className={styles.controls}>
        {torchAvailable && (
          <Button
            className={`${styles.ghost} ${torchOn ? styles.lit : ''}`}
            aria-pressed={torchOn}
            onClick={() => {
              const next = !torchOn;
              setTorchOn(next);
              void session.current?.setTorch(next);
            }}
          >
            {torchOn ? strings.scan.torchOff : strings.scan.torchOn}
          </Button>
        )}
        {photo}
        <Button className={styles.ghost} icon={<KeyIcon />} onClick={() => setMode('manual')}>
          {strings.scan.manualToggle}
        </Button>
      </div>
    </div>
  );
}
